import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Amount from 'design-system/Amount';
import { rem, Spacer } from 'design-system';
import StatusIcon from 'design-system/StatusIcon';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconStatusPaused from 'design-system/icons/status/IconStatusPaused';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { formatDateForItem } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { IsaAllowanceFormatted, JisaAllowanceFormatted } from '../constants';
const AutoinvestActivityCard = ({ activity, btnStyles }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const isFailed = activity.status === 'cancelled' || activity.status === 'skipped';
    const isPositive = activity.status === 'completed' || activity.status === 'partially_completed';
    const amountColor = isPositive ? colors.text.income : isFailed ? colors.text.tertiary : undefined;
    const handlePress = useCallback(() => {
        if (activity.status === 'completed' || activity.status === 'partially_completed') {
            navigation.navigate('AutoinvestActivityTimeline', {
                activity: activity,
            });
            return;
        }
        if (activity.status === 'skipped') {
            Modal.showConfirmation({
                title: 'Autoinvest skipped',
                subtitle: 'You\u2019ve opted to skip the scheduled direct debit collection, so the amount has not been deducted. The option to reverse the skip is only accessible for the first 24 hours.',
                confirmButtonTitle: 'Got it',
            });
        }
        else if (activity.status === 'cancelled') {
            if (activity?.statusReason === 'isa_allowance_exceeded' || activity?.statusReason === 'jisa_allowance_exceeded') {
                Modal.showConfirmation({
                    title: 'Autoinvest cancelled',
                    subtitle: (<Text TextThin-14 Secondary centered>
              We couldn’t complete this Autoinvest because you’ve reached your annual allowance. You’ve already
              deposited{' '}
              {activity.statusReason === 'isa_allowance_exceeded' ? IsaAllowanceFormatted : JisaAllowanceFormatted} this
              tax year. No money was taken.
            </Text>),
                    confirmButtonTitle: 'Got it',
                });
            }
            else {
                Modal.showConfirmation({
                    title: 'Autoinvest cancelled',
                    subtitle: 'We couldn\u2019t complete this Autoinvest. No money was taken.',
                    confirmButtonTitle: 'Got it',
                });
            }
        }
    }, [activity]);
    const content = useMemo(() => (<View style={styles.mainRow}>
        <View style={styles.iconContainer}>
          <IconRocket colorKey="brand"/>
        </View>
        <Spacer.W16 />
        <View style={styles.flex}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16>Autoinvest</Text>
              {activity.status === 'completed' && (<View style={styles.row}>
                  <StatusIcon height={16} width={16} state="done"/>
                  <Spacer w={8}/>
                  <Text TextThin-14 Green numberOfLines={1}>
                    Completed
                  </Text>
                </View>)}
              {activity.status === 'partially_completed' && (<View style={styles.row}>
                  <StatusIcon height={16} width={16} state="done"/>
                  <Spacer w={8}/>
                  <Text TextThin-14 Green numberOfLines={1}>
                    Partially completed
                  </Text>
                </View>)}
              {activity.status === 'skipped' && (<View style={styles.row}>
                  <IconStatusPaused width={16} height={16} color={colors.cards.onDark} bgColor={colors.elements.secondary}/>
                  <Spacer w={8}/>
                  <Text TextThin-14 Secondary numberOfLines={1}>
                    Skipped
                  </Text>
                </View>)}
              {activity.status === 'cancelled' && (<View style={styles.row}>
                  <StatusIcon height={16} width={16} state="cancelled-red"/>
                  <Spacer w={8}/>
                  <Text TextThin-14 Negative numberOfLines={1}>
                    Cancelled
                  </Text>
                </View>)}
            </View>
            <View style={styles.rightContent}>
              <Amount showSign Numbers-16 value={activity.amount} style={isFailed ? styles.skipped : {}} color={amountColor}/>
              <Text TextThin-14 Secondary>
                {formatDateForItem(new Date(activity.timestamp))}
              </Text>
            </View>
          </View>
        </View>
      </View>), [
        styles.mainRow,
        styles.iconContainer,
        styles.flex,
        styles.row,
        styles.rightContent,
        styles.skipped,
        activity.status,
        activity.amount,
        activity.timestamp,
        colors.cards.onDark,
        colors.elements.secondary,
        isFailed,
        amountColor,
    ]);
    return (<TouchableHighlight hoverOnLight activeOpacity={1} underlayColor={colors.background.underlay} style={btnStyles} onPress={handlePress}>
      {content}
    </TouchableHighlight>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    mainRow: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    iconContainer: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        borderRadius: rem(42),
        alignItems: 'center',
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.brandLight,
    },
    rightContent: {
        alignItems: 'flex-end',
    },
    skipped: {
        textDecorationStyle: 'solid',
        textDecorationLine: 'line-through',
    },
}));
export default memo(AutoinvestActivityCard);
